import { EndpointCallFactory, EndpointResponse } from "../http/endpoint";
import { LoadingState, LoadingStateDefaults, LoadingStateStoreApi, LoadingStateModifiers } from "./loadingstate";
import { Container } from "../state/types";

type StateProducer<S extends LoadingState> =
    (targetDraftState: S, loadedState: Container, response?: EndpointResponse) => void;

const EmptyStateProducer =
    <S extends LoadingState>(s: S, c: Container) => {};

const loadImmediate = async function <S extends LoadingState>(
    store: LoadingStateStoreApi<S>,
    loader: EndpointCallFactory,
    stateProducer: StateProducer<S>)
{
    store.setState(state =>
        LoadingStateModifiers.startLoading(state)
    );

    const resolve = (success: boolean, response: EndpointResponse) =>
    {
        store.setState(state =>
        {
            if (state.loadQueued) {
                state.loadQueued = false;
                setTimeout(() => loadImmediate(store, loader, stateProducer), 0);
            } else {
                stateProducer(state, response.data, response);
                LoadingStateModifiers.stopLoading(state, success);
            }
        });
    }

    const promise = loader();

    return promise.then(
        (success: EndpointResponse) => resolve(true, success),
        (failure: EndpointResponse) => resolve(false, failure)
    );
}

const load = async function <S extends LoadingState>(
    store: LoadingStateStoreApi<S>,
    loader: EndpointCallFactory,
    stateProducer: StateProducer<S>)
{
    const { loading } = store.getState();
    if (!loading) {
        loadImmediate(store, loader, stateProducer);
    }
}

const loadLast = async function <S extends LoadingState>(
    store: LoadingStateStoreApi<S>,
    loader: EndpointCallFactory,
    stateProducer: StateProducer<S>)
{
    const { loading } = store.getState();
    if (loading) {
        store.setState(draft =>
        {
            draft.loadQueued = true;
        });
    } else {
        loadImmediate(store, loader, stateProducer);
    }
}

const update = async function <S extends LoadingState>(
    store: LoadingStateStoreApi<S>,
    updater: EndpointCallFactory,
    stateProducer: StateProducer<S> = null,
    failureStateProducer: StateProducer<S> = null)
// if failureStateProducer is present, stateProducer is called before remote call
{
    const { updating } = store.getState();
    if (updating) {
        return;
    }

    const setState = stateProducer !== null;
    const setStateBeforeRPC = setState && failureStateProducer !== null;
    const setStateAfterRPC = setState && failureStateProducer === null;

    store.setState(state =>
    {
        LoadingStateModifiers.startUpdating(state)

        if (setStateBeforeRPC) {
            stateProducer(state, {}, undefined);
        }
    });

    const resolve = (success: boolean, response: EndpointResponse) =>
    {
        store.setState(state =>
        {
            if (setStateAfterRPC) {
                stateProducer(state, response.data, response);
            } else if (success) {
                // fall down
            } else if (failureStateProducer !== null) {
                failureStateProducer(state, response.data, response);
            }
            LoadingStateModifiers.stopUpdating(state, success)
        });
    }

    const promise = updater();

    return promise.then(
        (success: EndpointResponse) => resolve(true, success),
        (failure: EndpointResponse) => resolve(false, failure)
    );
}

export
{
    EmptyStateProducer,
    load,
    loadLast,
    LoadingState,
    LoadingStateDefaults,
    LoadingStateStoreApi,
    update,
    StateProducer
}